import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AppLayout } from './components/Layout/AppLayout';
import { AuthGate } from './components/AuthGate';
import { OverviewPage } from './pages/OverviewPage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import BillingPage from './pages/BillingPage';
import { SettingsPage } from './pages/SettingsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppBridgeProvider } from './contexts/AppBridgeContext';
import { ToastProvider } from './contexts/ToastContext';
import { BillingProvider } from './contexts/BillingContext';

function AuthenticatedLayout() {
  return (
    <AuthGate>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </AuthGate>
  );
}

function PublicLegalLayout() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <Outlet />
    </div>
  );
}

/**
 * Router basename, accounting for Shopify's admin deep-link prefix.
 *
 * When the merchant reaches us through an admin deep link
 * (`admin.shopify.com/store/<handle>/apps/<client-id>/billing` — which is where
 * billing approvals return to), Shopify forwards the `/apps/<client-id>` path
 * segment into the iframe src. Without a basename the router sees
 * `/apps/<client-id>/billing`, matches nothing, and the catch-all bounces the
 * merchant to Overview — so approving a charge would silently land them on the
 * wrong page instead of the billing confirmation.
 *
 * Computed from the live pathname rather than hard-coded so the direct
 * `pages.dev` entry point (no prefix) keeps working unchanged.
 */
function routerBasename(): string {
  const match = window.location.pathname.match(/^\/apps\/[^/]+/);
  return match ? match[0] : '';
}

function App() {
  return (
    <ErrorBoundary>
      <AppBridgeProvider>
        <ToastProvider>
          <BillingProvider>
            <BrowserRouter
              basename={routerBasename()}
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Routes>
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                {/* Legal pages must be publicly reachable (App Store listing
                    links to them), so they live outside the auth gate. */}
                <Route element={<PublicLegalLayout />}>
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                </Route>
                <Route element={<AuthenticatedLayout />}>
                  <Route path="/" element={<OverviewPage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/billing" element={<BillingPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </BillingProvider>
        </ToastProvider>
      </AppBridgeProvider>
    </ErrorBoundary>
  );
}

export default App;
